import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Icon from "@insite/mobius/Icon";
import Img from "@insite/mobius/Img";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowDownBlue from "@tCentro/Icons/ArrowDownBlue";
import { BrandProductFamily, BrandProductFamilyChildProduct } from "@tCentro/Services/CENBrandDetailsProductFamilesService";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import { getBrandProductFamilies } from "@tCentro/Store/Reducer/BrandProductFamiliesSelectors";
import React, { FC, useContext, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    brandId?: string;
}

const mapStateToProps = (state: CustomApplicationState, ownProps: OwnProps) => {
    return {
        families: getBrandProductFamilies(state, ownProps.brandId)?.families,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CENBrandDetailsProductFamiliesStyles {
    container?: InjectableCss;
    title?: InjectableCss;
    familyList?: InjectableCss;
    familyBlock?: InjectableCss;
    familyCard?: InjectableCss;
    familyMeta?: InjectableCss;
    thumb?: InjectableCss;
    familyText?: InjectableCss;
    familyName?: InjectableCss;
    familyDescription?: InjectableCss;
    toggleLink?: InjectableCss;
    chevron?: InjectableCss;
    primaryAction?: InjectableCss;
    primaryActionDesktop?: InjectableCss;
    primaryActionMobile?: InjectableCss;
    tableWrap?: InjectableCss;
    productTable?: InjectableCss;
    tableHead?: InjectableCss;
    tableHeaderCell?: InjectableCss;
    tableRow?: InjectableCss;
    tableCell?: InjectableCss;
    descriptionText?: InjectableCss;
    descriptionToggle?: InjectableCss;
    productLinkCell?: InjectableCss;
    productLink?: InjectableCss;
}

export const contentStyles: CENBrandDetailsProductFamiliesStyles = {
    container: {
        css: css`
            width: calc(100% - 40px);
            max-width: calc(100% - 40px);
            margin: 0 20px;
            padding: 0;
            background: #ffffff;
            color: #1f2d2e;
            @media (max-width: 768px) {
                width: 100%;
                max-width: 100%;
                margin: 0;
            }
        `,
    },

    title: {
        css: css`
            margin: 0 0 18px;
            font-size: 32px;
            line-height: 40px;
            font-weight: 700;
            text-align: center;
            @media (max-width: 768px) {
                font-size: 24px;
                line-height: 32px;
                font-weight: 600;
            }
        `,
    },

    familyList: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 0;
        `,
    },

    familyBlock: {
        css: css`
            border: 1px solid #BDBBBD;
            margin-top: -1px;
            &[data-expanded="true"] {
                background: #F3F7F4;
            }
        `,
    },

    familyCard: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 16px;
            padding: 24px 32px;
            @media (max-width: 768px) {
                padding: 16px;
                gap: 8px;
            }
        `,
    },

    familyMeta: {
        css: css`
            display: flex;
            align-items: flex-start;
            gap: 16px;
            flex: 1 1 auto;
            min-width: 0;
        `,
    },

    thumb: {
        css: css`
            width: 104px;
            height: 104px;
            overflow: hidden;
            box-shadow: 0px 2px 8px 0px #26252529;
            flex-shrink: 0;
            background: #fff;
            @media (max-width: 768px) {
                width: 80px;
                height: 80px;
            }

            img {
                width: 100%;
                height: 100%;
                object-fit: contain;
            }
        `,
    },

    familyText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
        `,
    },

    familyName: {
        css: css`
            margin: 0 0 4px;
            font-size: 18px;
            line-height: 24px;
            font-weight: 600;
            @media (max-width: 768px) {
                font-size: 14px;
                line-height: 16px;
            }
        `,
    },

    familyDescription: {
        css: css`
            margin: 0;
            color: #5b6b6c;
            font-size: 18px;
            line-height: 24px;
            max-width: 760px;
            @media (max-width: 768px) {
                font-size: 14px;
            }
        `,
    },

    toggleLink: {
        css: css`
            display: inline-flex;
            align-items: center;
            gap: 6px;
            margin-top: 8px;
            padding: 0;
            border: none;
            background: none;
            color: #0F96D9;
            text-decoration: none;
            font-size: 18px;
            line-height: 24px;
            font-weight: 600;
            cursor: pointer;

             @media (max-width: 768px) {
                font-size: 12px;
                line-height: 16px;
            }

            &:hover {
                text-decoration: underline;
            }
        `,
    },

    chevron: {
        css: css`
            transition: transform 0.15s ease;

            &[data-expanded="true"] {
                transform: rotate(180deg);
            }
        `,
    },

    primaryAction: {
        css: css`
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 8px 16px;
            border: 2px solid #266B40;
            border-radius: 999px;
            background: transparent;
            color: #266B40;
            text-decoration: none;
            font-size: 14px;
            line-height: 1;
            font-weight: 600;
            white-space: nowrap;
            min-width: 123px;
            flex-shrink: 0;
            gap: 4px;
            align-self: center;

            @media (max-width: 768px) {
                font-size: 12px;
                padding: 4px 12px;
                min-width: auto;
                margin: 2px 0 6px;
            }

            &:hover {
                background: #eef7f2;
                text-decoration: none;
            }
        `,
    },
    primaryActionDesktop: {
        css: css`
            align-self: center;
            @media (max-width: 768px) {
                display: none;
            }
        `,
    },
    primaryActionMobile: {
        css: css`
            @media (min-width: 769px) {
                display: none;
            }
        `,
    },

    tableWrap: {
        css: css`
            margin: 0;
            border: 1px solid #d8d9d7;
            overflow: hidden;
            border-radius: 2px;

            &[data-expanded="false"] {
                display: none;
            }

            @media (max-width: 768px) {
                overflow-x: auto;
            }
        `,
    },

    productTable: {
        css: css`
            width: 100%;
            border-collapse: collapse;
            @media (max-width: 768px) {
                width: 800px;
            }
        `,
    },

    tableHead: {
        css: css`
            background: #266B40;
            color: #fff;
        `,
    },

    tableHeaderCell: {
        css: css`
            padding: 12px 14px;
            text-align: left;
            font-size: 18px;
            line-height: 24px;
            font-weight: 600;
            color: #fff;
            background: #266B40;
            min-width: 160px;
            @media (max-width: 768px) {
                font-size: 14px;
                line-height: 16px;
            }
        `,
    },

    tableRow: {
        css: css`
            background: #ffffff;

            &[data-alt="true"] {
                background: #F2F2F2;
            }
        `,
    },

    tableCell: {
        css: css`
            padding: 12px 14px;
            vertical-align: top;
            border-bottom: 1px solid #e5e5e2;
            font-size: 14px;
            line-height: 20px;
            color: #2d3d3b;
            &:first-child {
                vertical-align: middle;
                font-weight: 600;
            }
        `,
    },

    descriptionText: {
        css: css`
            display: block;

            &[data-collapsed="true"] {
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 2;
                overflow: hidden;
            }
        `,
    },

    descriptionToggle: {
        css: css`
            margin-left: 4px;
            padding: 0;
            border: none;
            background: none;
            color: #0F96D9;
            font-size: inherit;
            line-height: inherit;
            cursor: pointer;

            &:hover {
                text-decoration: underline;
            }
        `,
    },

    productLinkCell: {
        css: css`
            text-align: right;
            white-space: nowrap;
            `,
    },

    productLink: {
        css: css`
            display: inline-flex;
            align-items: center;
            justify-content: flex-end;
            color: #0F96D9;
            text-decoration: none;
            font-weight: 600;
            font-size: 14px;
            line-height: 1;
            white-space: nowrap;
            padding-right: 32px;
            gap: 4px;

            &:hover {
                text-decoration: underline;
            }
        `,
    },
};

const styles = contentStyles;

const BrandDetailsProductFamilies: FC<Props> = ({ brandId, families = [] }) => {
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const [expandedDescriptions, setExpandedDescriptions] = useState<Record<string, boolean>>({});
    const [expandableDescriptions, setExpandableDescriptions] = useState<Record<string, boolean>>({});
    const descriptionRefs = useRef<Record<string, HTMLSpanElement | null>>({});
    // Client-only measuring of clamped descriptions (correct to keep in useEffect for SSR)
    useEffect(() => {
        const updateExpandableDescriptions = () => {
            setExpandableDescriptions(previous => {
                const next = { ...previous };
                let hasChanges = false;

                Object.entries(descriptionRefs.current).forEach(([key, element]) => {
                    if (!element || expandedDescriptions[key]) {
                        return;
                    }

                    const isExpandable = element.scrollHeight > element.clientHeight;
                    if (next[key] !== isExpandable) {
                        next[key] = isExpandable;
                        hasChanges = true;
                    }
                });

                return hasChanges ? next : previous;
            });
        };

        updateExpandableDescriptions();
        window.addEventListener("resize", updateExpandableDescriptions);
        return () => window.removeEventListener("resize", updateExpandableDescriptions);
    }, [families, expandedIndex, expandedDescriptions]);

    if (!brandId) {
        return null;
    }

    const toggleFamily = (index: number) => {
        setExpandedIndex(prev => (prev === index ? null : index));
    };

    const toggleDescription = (key: string) => {
        setExpandedDescriptions(prev => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <StyledWrapper as="section" {...styles.container} data-test-selector="brandProductFamilies" id="brand-product-families">
            <StyledWrapper as="h2" {...styles.title}>Product Families</StyledWrapper>
            <StyledWrapper as="div" {...styles.familyList}>
                {families.map((family: BrandProductFamily, index: number) => {
                    const isExpanded = expandedIndex === index;
                    const products: BrandProductFamilyChildProduct[] = family.childProducts ?? [];

                    return (
                        <StyledWrapper
                            as="div"
                            {...styles.familyBlock}
                            key={`${family.familyName}-${index}`}
                            data-expanded={isExpanded}
                        >
                            <StyledWrapper as="div" {...styles.familyCard}>
                                <StyledWrapper as="div" {...styles.familyMeta}>
                                    <StyledWrapper as="div" {...styles.thumb}>
                                        {family.featuredImagePath ? (
                                            <Img src={family.featuredImagePath} alt={family.familyName} loading="lazy" />
                                        ) : null}
                                    </StyledWrapper>
                                    <StyledWrapper as="div" {...styles.familyText}>
                                        <StyledWrapper as="h3" {...styles.familyName}>{family.familyName}</StyledWrapper>
                                        <StyledWrapper as="div" {...styles.primaryActionMobile}>
                                            <StyledWrapper
                                                as="a"
                                                {...styles.primaryAction}
                                                href={family.parentProductUrl || "#"}
                                            ><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M11.8755 2.85642C11.8075 2.57306 11.4478 2.47837 11.2418 2.68443L9.44766 4.47881L7.5 4.4788V2.53115L9.29414 0.736774C9.50018 0.530781 9.40547 0.170992 9.1222 0.103024C8.56697 -0.0304061 7.9658 -0.040203 7.34283 0.108845C5.90259 0.453377 4.77877 1.67681 4.54908 3.13931C4.42711 3.74704 4.4857 4.35407 4.66852 4.90251L0.494297 9.07673C-0.164766 9.73579 -0.164766 10.8038 0.494297 11.4627C0.802734 11.7931 1.23398 11.9783 1.64508 11.9783C2.05617 11.9783 2.50852 11.8135 2.83828 11.484L7.0125 7.30978C7.56234 7.49109 8.1682 7.54978 8.79773 7.4505C10.2605 7.21987 11.4837 6.09581 11.8282 4.65675C12.0185 4.01189 12.0091 3.41189 11.8755 2.85642ZM1.85367 10.6658C1.54313 10.6658 1.29117 10.4138 1.29117 10.1033C1.29117 9.79251 1.54313 9.54079 1.85367 9.54079C2.16422 9.54079 2.41617 9.79251 2.41617 10.1033C2.41617 10.415 2.16422 10.6658 1.85367 10.6658Z" fill="#266B40" />
                                                </svg>Configure</StyledWrapper>
                                        </StyledWrapper>
                                        {family.familyDescription ? (
                                            <StyledWrapper as="p" {...styles.familyDescription}>{family.familyDescription}</StyledWrapper>
                                        ) : null}
                                        {products.length > 0 && (
                                            <StyledWrapper
                                                as="button"
                                                {...styles.toggleLink}
                                                type="button"
                                                onClick={() => toggleFamily(index)}
                                                aria-expanded={isExpanded}
                                            >
                                                {isExpanded ? "Hide popular configurations" : "View popular configurations"}
                                                <Icon src={ArrowDownBlue} data-expanded={isExpanded} size={12} {...styles.chevron} />
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                                <StyledWrapper as="div" {...styles.primaryActionDesktop}>
                                    <StyledWrapper
                                        as="a"
                                        {...styles.primaryAction}
                                        href={family.parentProductUrl || "#"}
                                    ><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M11.8755 2.85642C11.8075 2.57306 11.4478 2.47837 11.2418 2.68443L9.44766 4.47881L7.5 4.4788V2.53115L9.29414 0.736774C9.50018 0.530781 9.40547 0.170992 9.1222 0.103024C8.56697 -0.0304061 7.9658 -0.040203 7.34283 0.108845C5.90259 0.453377 4.77877 1.67681 4.54908 3.13931C4.42711 3.74704 4.4857 4.35407 4.66852 4.90251L0.494297 9.07673C-0.164766 9.73579 -0.164766 10.8038 0.494297 11.4627C0.802734 11.7931 1.23398 11.9783 1.64508 11.9783C2.05617 11.9783 2.50852 11.8135 2.83828 11.484L7.0125 7.30978C7.56234 7.49109 8.1682 7.54978 8.79773 7.4505C10.2605 7.21987 11.4837 6.09581 11.8282 4.65675C12.0185 4.01189 12.0091 3.41189 11.8755 2.85642ZM1.85367 10.6658C1.54313 10.6658 1.29117 10.4138 1.29117 10.1033C1.29117 9.79251 1.54313 9.54079 1.85367 9.54079C2.16422 9.54079 2.41617 9.79251 2.41617 10.1033C2.41617 10.415 2.16422 10.6658 1.85367 10.6658Z" fill="#266B40" />
                                        </svg>Configure</StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>

                            {
                                products.length > 0 && (
                                    <StyledWrapper as="div" {...styles.tableWrap} data-expanded={isExpanded}>
                                        <StyledWrapper as="table" {...styles.productTable}>
                                            <StyledWrapper as="thead" {...styles.tableHead}>
                                                <tr>
                                                    <StyledWrapper as="th" {...styles.tableHeaderCell}>Part Number</StyledWrapper>
                                                    <StyledWrapper as="th" {...styles.tableHeaderCell}>Product</StyledWrapper>
                                                    <StyledWrapper as="th" {...styles.tableHeaderCell}>Product Details</StyledWrapper>
                                                    <StyledWrapper as="th" {...styles.tableHeaderCell} aria-hidden="true" />
                                                </tr>
                                            </StyledWrapper>
                                            <tbody>
                                                {products.map((product, productIndex) => {
                                                    const description = product.productLongDescription ?? "";
                                                    const descriptionKey = `${index}-${product.productId || productIndex}`;
                                                    const isDescriptionExpanded = expandedDescriptions[descriptionKey];
                                                    const hasExpandableDescription = expandableDescriptions[descriptionKey];

                                                    return (
                                                        <StyledWrapper
                                                            as="tr"
                                                            {...styles.tableRow}
                                                            key={product.productId || `${product.partNumber}-${productIndex}`}
                                                            data-alt={productIndex % 2 === 1}
                                                        >
                                                            <StyledWrapper as="td" {...styles.tableCell}>{product.partNumber}</StyledWrapper>
                                                            <StyledWrapper as="td" {...styles.tableCell}>{product.productShortDescription}</StyledWrapper>
                                                            <StyledWrapper as="td" {...styles.tableCell}>
                                                                <StyledWrapper
                                                                    as="span"
                                                                    {...styles.descriptionText}
                                                                    data-collapsed={!isDescriptionExpanded}
                                                                    ref={(element: HTMLSpanElement | null) => {
                                                                        descriptionRefs.current[descriptionKey] = element;
                                                                    }}
                                                                >
                                                                    {description}
                                                                </StyledWrapper>
                                                                {hasExpandableDescription && (
                                                                    <StyledWrapper
                                                                        as="button"
                                                                        {...styles.descriptionToggle}
                                                                        type="button"
                                                                        onClick={() => toggleDescription(descriptionKey)}
                                                                    >
                                                                        {isDescriptionExpanded ? "Show less" : "Show more"}
                                                                    </StyledWrapper>
                                                                )}
                                                            </StyledWrapper>
                                                            <StyledWrapper as="td" {...styles.tableCell} {...styles.productLinkCell}>
                                                                <StyledWrapper as="a" {...styles.productLink} href={product.productUrl}>
                                                                    View Product
                                                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M11.7797 4.27981L8.77969 7.27981C8.48648 7.57301 8.01188 7.57254 7.71914 7.27981C7.42617 6.98684 7.42617 6.51223 7.71914 6.21926L9.44062 4.50012H0.75C0.335391 4.50012 0 4.16496 0 3.72902C0 3.29309 0.335391 3.00012 0.75 3.00012H9.44062L7.72078 1.28027C7.42781 0.987305 7.42781 0.512695 7.72078 0.219727C8.01375 -0.0732422 8.48836 -0.0732422 8.78133 0.219727L11.7813 3.21973C12.0727 3.5134 12.0727 3.98684 11.7797 4.27981Z" fill="#0F96D9" />
                                                                    </svg>

                                                                </StyledWrapper>
                                                            </StyledWrapper>
                                                        </StyledWrapper>
                                                    );
                                                })}
                                            </tbody>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )
                            }
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const ConnectedBrandDetailsProductFamilies = connect(mapStateToProps)(BrandDetailsProductFamilies);

// The brand id comes from context, so this thin wrapper passes it to mapStateToProps as an own prop.
const BrandDetailsProductFamiliesWidget: FC = () => {
    const { value: brand } = useContext(BrandStateContext);
    return <ConnectedBrandDetailsProductFamilies brandId={brand?.id} />;
};

const widgetModule: WidgetModule = {
    component: BrandDetailsProductFamiliesWidget,
    definition: {
        group: "Brand Details",
        displayName: "CEN Brand Product Families",
    },
};

export default widgetModule;